import React, {
  createContext,
  MouseEvent,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { DataRow, isDataRow, Row } from '@paragon/layout-models';
import { getModifierKeys } from '@paragon/utilities';

import { isSelectableItemType, SelectableItem, SelectableItemType } from '../../../../library/selection/types';
import { selectItems } from '../../../../store/actions/selectActions';
import { isMultiSelectableItemTypeSelector } from '../../../../store/selectors/isMultiSelectableItemTypeSelector';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { isComponentDesignRow } from './schemas/CustomTrussDesignSchema';

function rowMatchesSelectionType(row: Row, selectionType: string): boolean {
  if (!isDataRow(row)) return false;

  if (selectionType === 'componentDesign') {
    return isComponentDesignRow(row);
  }

  return row.type === selectionType;
}

function collectMatchingItems(rows: Row[], startIndex: number, endIndex: number, selectionType: string) {
  const items: SelectableItem[] = [];
  for (let i = startIndex; i <= endIndex; i++) {
    const row = rows[i];
    if (rowMatchesSelectionType(row, selectionType) && isDataRow(row)) {
      items.push((row as DataRow<SelectableItem>).item);
    }
  }
  return items;
}

interface ShiftSelectContextValue {
  /**
   * Handles a click on a navigation panel row, with support for shift-click range selection.
   *
   * Returns `true` if the click was handled as a shift-select (the caller should not perform its own selection logic).
   * Returns `false` if the click was not a shift-select and the caller should handle it normally.
   */
  handleRowClick: (event: MouseEvent, rowId: string, selectionType: string) => boolean;

  /**
   * Updates the anchor to the given row. Call this after a normal (non-shift) click completes selection.
   */
  setAnchor: (rowId: string, selectionType: string) => void;
}

const ShiftSelectContext = createContext<ShiftSelectContextValue | null>(null);

export function useShiftSelect(): ShiftSelectContextValue | null {
  return useContext(ShiftSelectContext);
}

interface ShiftSelectProviderProps {
  visibleRowsRef: RefObject<Row[]>;
  children: React.ReactNode;
}

export function ShiftSelectProvider({ visibleRowsRef, children }: ShiftSelectProviderProps) {
  const dispatch = useAppDispatch();
  const isMultiSelectableItemType = useAppSelector(isMultiSelectableItemTypeSelector);
  const anchorRef = useRef<{ rowId: string; selectionType: string } | null>(null);

  const setAnchor = useCallback((rowId: string, selectionType: string) => {
    anchorRef.current = { rowId, selectionType };
  }, []);

  const handleRowClick = useCallback(
    (event: MouseEvent, rowId: string, selectionType: string): boolean => {
      const { shiftKey, ctrlKey } = getModifierKeys(event);

      if (!shiftKey || !anchorRef.current) {
        return false;
      }

      if (!isSelectableItemType(selectionType) || !isMultiSelectableItemType(selectionType)) {
        return false;
      }

      // Shift-select only works between items of the same selection type
      if (anchorRef.current.selectionType !== selectionType) {
        return false;
      }

      const anchor = anchorRef.current;
      const visibleRows = visibleRowsRef.current;

      const anchorIndex = visibleRows.findIndex(
        (row) => isDataRow(row) && row.id === anchor.rowId && rowMatchesSelectionType(row, selectionType),
      );
      const clickedIndex = visibleRows.findIndex(
        (row) => isDataRow(row) && row.id === rowId && rowMatchesSelectionType(row, selectionType),
      );

      if (anchorIndex === -1 || clickedIndex === -1) {
        return false;
      }

      const itemsInRange = collectMatchingItems(
        visibleRows,
        Math.min(anchorIndex, clickedIndex),
        Math.max(anchorIndex, clickedIndex),
        selectionType,
      );

      if (itemsInRange.length === 0) {
        return false;
      }

      dispatch(
        selectItems(itemsInRange, selectionType as SelectableItemType, {
          intent: 'select',
          exclusive: !ctrlKey,
        }),
      );

      return true;
    },
    [dispatch, isMultiSelectableItemType, visibleRowsRef],
  );

  // Ctrl+Shift+Home: select from anchor to first row; Ctrl+Shift+End: select from anchor to last row
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const { ctrlKey, shiftKey } = getModifierKeys(event);

      if (!ctrlKey || !shiftKey) return;
      if (event.code !== 'Home' && event.code !== 'End') return;
      if (!anchorRef.current) return;

      const anchor = anchorRef.current;
      const { selectionType } = anchor;

      if (!isSelectableItemType(selectionType) || !isMultiSelectableItemType(selectionType)) return;

      const visibleRows = visibleRowsRef.current;

      const anchorIndex = visibleRows.findIndex(
        (row) => isDataRow(row) && row.id === anchor.rowId && rowMatchesSelectionType(row, selectionType),
      );

      if (anchorIndex === -1) return;

      const items =
        event.code === 'Home'
          ? collectMatchingItems(visibleRows, 0, anchorIndex, selectionType)
          : collectMatchingItems(visibleRows, anchorIndex, visibleRows.length - 1, selectionType);

      if (items.length === 0) return;

      event.preventDefault();
      event.stopPropagation();

      dispatch(
        selectItems(items, selectionType as SelectableItemType, {
          intent: 'select',
          exclusive: true,
        }),
      );
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dispatch, isMultiSelectableItemType, visibleRowsRef]);

  const contextValue = useMemo(() => ({ handleRowClick, setAnchor }), [handleRowClick, setAnchor]);

  return <ShiftSelectContext.Provider value={contextValue}>{children}</ShiftSelectContext.Provider>;
}
